// Immune Resilience self-check — ORIGINAL wording, non-diagnostic. 15 items across
// 4 dimensions. Band scoring: each option 0–3, summed across 15 questions (max 45)
// → 3 resilience bands.
const immune = {
  slug: "immune",
  title: "How resilient is your immune system?",
  emoji: "🛡️",
  blurb: "A 15-item check across 4 dimensions to gauge your immune resilience and the herbs that support it.",
  seoTitle: "Immune Health Quiz: How Resilient Are You? | Sarv",
  seoDescription:
    "Take the free immune resilience quiz to see where you stand — resilient, run-down, or depleted — and the herbs traditionally used to support immune health.",
  keywords:
    "immune health quiz, immune resilience, herbs for immune system, natural immune support, always getting sick, boost immunity naturally",
  intro:
    "15 questions across how often you get sick, how you recover, and the stress, sleep, and lifestyle factors behind immunity. A general wellness check, not a medical diagnosis.",
  evidenceNote:
    "This assessment is built around the factors research links to immune resilience — how often you get sick and recover, plus sleep, chronic stress, and lifestyle, which are all documented to shape immune function. Some traditional immune herbs (elderberry, astragalus, echinacea) have systematic-review evidence, though results vary — the balanced NIH/NCCIH sources below explain the current state of the science. This is a general wellness check, not a medical test.",
  references: [
    {
      label: "The bidirectional relationship between sleep and immunity against infections",
      publisher: "PMC / NIH",
      url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC4568388/",
    },
    {
      label: "Enhancing versus suppressive effects of stress on immune function",
      publisher: "PMC / NIH",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC2790771/",
    },
    {
      label: "Elderberry for prevention and treatment of viral respiratory illnesses: a systematic review",
      publisher: "PMC / NIH",
      url: "https://www.ncbi.nlm.nih.gov/pmc/articles/PMC8026097/",
    },
    {
      label: "Echinacea in respiratory infections: a mini-review of randomized controlled trials",
      publisher: "PMC / NIH",
      url: "https://pmc.ncbi.nlm.nih.gov/articles/PMC8879308/",
    },
    {
      label: "Astragalus: Usefulness and Safety",
      publisher: "NCCIH / NIH",
      url: "https://www.nccih.nih.gov/health/astragalus",
    },
    {
      label: "The effect of astragalus on humoral and cellular immune response: a systematic review & meta-analysis",
      publisher: "PubMed / NIH",
      url: "https://pubmed.ncbi.nlm.nih.gov/37952511/",
    },
  ],
  scoring: {
    mode: "band",
    max: 45,
    bands: [
      { max: 13, key: "resilient" },
      { max: 29, key: "rundown" },
      { max: 45, key: "depleted" },
    ],
  },
  questions: [
    // --- Getting sick ---
    {
      section: "Getting sick",
      question: "How often do you catch colds or bugs?",
      options: [
        { label: "Rarely", value: 0 },
        { label: "A couple of times a year", value: 1 },
        { label: "Often", value: 2 },
        { label: "Constantly, and they linger", value: 3 },
      ],
    },
    {
      section: "Getting sick",
      question: "When something's going around, you…",
      options: [
        { label: "Usually dodge it", value: 0 },
        { label: "Sometimes catch it", value: 1 },
        { label: "Usually catch it", value: 2 },
        { label: "Always catch it, and it hits hard", value: 3 },
      ],
    },
    {
      section: "Getting sick",
      question: "How often do you get lingering coughs, sniffles, or low-grade symptoms?",
      options: [
        { label: "Rarely", value: 0 },
        { label: "Occasionally", value: 1 },
        { label: "Often", value: 2 },
        { label: "Almost constantly", value: 3 },
      ],
    },
    {
      section: "Getting sick",
      question: "When you travel or hit a stressful stretch, do you tend to get sick?",
      options: [
        { label: "Rarely", value: 0 },
        { label: "Sometimes", value: 1 },
        { label: "Usually", value: 2 },
        { label: "Almost always", value: 3 },
      ],
    },
    // --- Recovery & resilience ---
    {
      section: "Recovery & resilience",
      question: "When you do get sick, recovery is…",
      options: [
        { label: "Fast — I bounce back", value: 0 },
        { label: "A few days", value: 1 },
        { label: "Drawn out", value: 2 },
        { label: "Weeks, and I feel wiped after", value: 3 },
      ],
    },
    {
      section: "Recovery & resilience",
      question: "How run-down do you feel day to day?",
      options: [
        { label: "Rarely", value: 0 },
        { label: "Occasionally", value: 1 },
        { label: "Often", value: 2 },
        { label: "Almost always", value: 3 },
      ],
    },
    {
      section: "Recovery & resilience",
      question: "Do cuts, wounds, or minor infections take a long time to heal?",
      options: [
        { label: "Rarely — I heal quickly", value: 0 },
        { label: "Occasionally", value: 1 },
        { label: "Often", value: 2 },
        { label: "Almost always", value: 3 },
      ],
    },
    {
      section: "Recovery & resilience",
      question: "Your overall energy and stamina?",
      options: [
        { label: "Strong", value: 0 },
        { label: "Okay", value: 1 },
        { label: "Low", value: 2 },
        { label: "Depleted", value: 3 },
      ],
    },
    // --- Stress & sleep ---
    {
      section: "Stress & sleep",
      question: "How have your stress levels been lately?",
      options: [
        { label: "Low and manageable", value: 0 },
        { label: "Moderate", value: 1 },
        { label: "High", value: 2 },
        { label: "Very high, for a long stretch", value: 3 },
      ],
    },
    {
      section: "Stress & sleep",
      question: "How's your sleep, most nights?",
      options: [
        { label: "Solid and restorative", value: 0 },
        { label: "Mostly okay", value: 1 },
        { label: "Often poor", value: 2 },
        { label: "Consistently poor", value: 3 },
      ],
    },
    {
      section: "Stress & sleep",
      question: "How rested do you feel when you wake up?",
      options: [
        { label: "Fully rested", value: 0 },
        { label: "Usually okay", value: 1 },
        { label: "Often tired", value: 2 },
        { label: "Rarely rested", value: 3 },
      ],
    },
    // --- Diet & lifestyle ---
    {
      section: "Diet & lifestyle",
      question: "How many servings of fruit, veg, and whole foods do you get most days?",
      options: [
        { label: "Plenty", value: 0 },
        { label: "A reasonable amount", value: 1 },
        { label: "Not many", value: 2 },
        { label: "Rarely any", value: 3 },
      ],
    },
    {
      section: "Diet & lifestyle",
      question: "How physically active are you in a typical week?",
      options: [
        { label: "Regularly active", value: 0 },
        { label: "Somewhat active", value: 1 },
        { label: "Rarely active", value: 2 },
        { label: "Almost never active", value: 3 },
      ],
    },
    {
      section: "Diet & lifestyle",
      question: "How has your digestion and gut health been?",
      options: [
        { label: "Solid", value: 0 },
        { label: "Mostly fine", value: 1 },
        { label: "Often off", value: 2 },
        { label: "Frequently troubled", value: 3 },
      ],
    },
    {
      section: "Diet & lifestyle",
      question: "How's your hydration and time outdoors / in daylight?",
      options: [
        { label: "Good", value: 0 },
        { label: "Okay", value: 1 },
        { label: "Poor", value: 2 },
        { label: "Very poor", value: 3 },
      ],
    },
  ],
  results: {
    resilient: {
      name: "Resilient",
      emoji: "🛡️",
      tagline: "Your immune system looks robust right now.",
      summary:
        "Your responses suggest your body's defenses are in good shape right now — you tend to shrug off what's going around and bounce back quickly when you do get ill. That's usually a sign the fundamentals are working for you: enough sleep, reasonable stress levels, regular movement, and a varied, nutrient-dense diet all feed a well-regulated immune system. The aim here isn't to fix anything but to protect what's working — and to lean on gentle, everyday immune-supportive herbs proactively, especially heading into cold-and-flu season or a stressful, travel-heavy stretch when even resilient systems get tested. Think of this as maintenance: keep the foundations strong and you keep the resilience.",
      focus: [
        "Keep the foundations strong: sleep, whole foods, and movement.",
        "Gentle, everyday herbs traditionally used to support immunity.",
        "Step up support proactively when a season or travel demands it.",
      ],
      herbs: ["Elderberry", "Garlic", "Green tea"],
      categorySlug: "immune-system-health",
    },
    rundown: {
      name: "Run-Down",
      emoji: "☁️",
      tagline: "Your defenses seem a little worn down.",
      summary:
        "Your responses suggest your immune resilience has slipped a notch — you may be catching more than usual, taking longer to recover, or feeling generally run-down. This pattern is very often downstream of the basics being stretched: broken sleep, a long run of stress (which keeps cortisol elevated and dampens parts of the immune response), or a diet that's light on the nutrients your body uses to defend itself. The encouraging part is that it's usually reversible. The most effective approach pairs immune-supportive, 'tonic' herbs with shoring up the fundamentals — protecting sleep, actively lowering stress, and eating nutrient-dense meals — so your body has both the signal and the raw materials to rebuild. Give it a few consistent weeks rather than expecting an overnight change.",
      focus: [
        "Prioritize sleep and stress recovery — both drive immunity.",
        "Herbs traditionally used to support and tone the immune system.",
        "Nutrient-dense meals and hydration to give your body materials to work with.",
      ],
      herbs: ["Elderberry", "Echinacea", "Astragalus"],
      categorySlug: "immune-system-health",
    },
    depleted: {
      name: "Depleted",
      emoji: "🔋",
      tagline: "Your responses point to low reserves and frequent illness.",
      summary:
        "Your responses point to low reserves and frequent illness — getting sick often, recovering slowly, and feeling depleted much of the time. This usually reflects a body that's been running on empty for a while, where chronic stress, poor sleep, and thin nutrition have compounded and left your defenses under-resourced. The focus here is genuine rebuilding rather than quick fixes: protected rest, consistent nourishment, and restorative 'tonic' herbs traditionally used to rebuild depleted immunity over time. Because persistent or severe illness can also have underlying medical causes, this is one result where we'd genuinely encourage you to check in with a healthcare professional alongside any natural support — not as an alarm, but so nothing treatable gets missed.",
      focus: [
        "Rebuild reserves with rest, nourishment, and consistent routines.",
        "Restorative, tonic herbs traditionally used for depleted immunity.",
        "See a professional if illness is frequent, severe, or persistent.",
      ],
      herbs: ["Astragalus", "Reishi", "Ashwagandha"],
      categorySlug: "immune-system-health",
    },
  },
};

export default immune;
